import * as R from 'ramda';
import {
  put,
  call,
  take,
  delay,
  select,
  takeLatest,
} from 'redux-saga/effects';
// constants
import * as C from '../../constants';
// utilities
import * as U from '../../utilities';
import { ENDPOINTS } from '../../utilities/http/endpoints';
// features
import { loginRoutine } from '../auth/routines';
import { loaderRoutine } from '../loader/routines';
import { makeSelectLoggedIn } from '../auth/selectors';
// component
import { makeSelectUsedReport } from '../../components/report-format-panel/selectors';
import { headerFileUploadStatusData } from '../../components/app-layout/header/routines';
import {
  fetchReportsListData,
  addUsedReportRoutine,
  editUsedReportRoutine,
  changeUsedReportRoutine,
  deleteUsedReportRoutine,
} from '../../components/report-format-panel/routines';
// feature board
import {
  makeSelectBoardList,
  makeSelectBoardListPagination,
} from './selectors';
import {
  boardRoutine,
} from './routines';
//////////////////////////////////////////////////

function* handleFetchBoardSaga({ payload }) {
  try {
    const page = R.pathOr(0, ['page'], payload);

    const { uuid: usedReportUUID } = yield select(makeSelectUsedReport());

    const usedReportPayloadUUID = R.path([C.FIELD_UUID], payload);

    const fetchReportsListRes = yield call(U.sendRequest, 'get', ENDPOINTS.reportConfig);

    const { data, error, status } = fetchReportsListRes;

    const defaultUuid = R.pathOr(usedReportUUID, [C.FIELD_UUID], R.head(data));

    const uuid = usedReportPayloadUUID || usedReportUUID || defaultUuid;

    if (U.isResponseSuccess(status)) {
      yield put(fetchReportsListData.success(data));

      if (U.isNotNilAndNotEmpty(uuid)) {
        const fetchReportRes = yield call(U.sendRequest, 'get', ENDPOINTS.getReportConfigByUUID(uuid));

        const { data: reportData, error: reportError, status: reportStatus } = fetchReportRes;

        const reqData = {
          page,
          size: 50,
          fields: R.pathOr([], ['fields'], reportData),
          orderFields: R.pathOr([], ['orderFields'], reportData),
          searchCriteria: R.pathOr([], ['searchCriteria'], reportData),
        };

        if (U.isResponseSuccess(reportStatus)) {
          yield put(changeUsedReportRoutine.success(reportData));

          const fetchBoardRes = yield call(U.sendRequest, 'post', ENDPOINTS.fetchBoard, { data: U.transformUsedReportValues(reqData) });

          const { data: fetchBoardData, error: fetchBoardError, status: fetchBoardStatus } = fetchBoardRes;

          if (U.isResponseSuccess(fetchBoardStatus)) {
            if (R.equals(0, page)) {
              yield put(boardRoutine.fetchSuccess(R.mergeLeft(fetchBoardData, { page, size: 50 })));
            } else {
              const boardList = yield select(makeSelectBoardList());

              yield put(boardRoutine.fetchSuccess(R.mergeLeft(
                R.evolve({ elements: R.concat(boardList) }, fetchBoardData),
                { page, size: 50 },
              )));
            }
            yield delay(1000);

            yield put(changeUsedReportRoutine.success(reportData));
          } else {
            yield put(boardRoutine.fetchFailure());

            yield call(U.showToastrMessage, 'error', fetchBoardError);
            yield call(U.handleFailResponse, fetchBoardRes, 'handleFetchBoardSaga fail', true);
          }
        } else {
          yield put(changeUsedReportRoutine.fetchFailure());

          yield call(U.showToastrMessage, 'error', reportError);
          yield call(U.handleFailResponse, fetchReportRes, 'handleFetchBoardSaga fail', true);
        }
      } else {
        const reqData = {
          page,
          size: 50,
          fields: [],
          orderFields: [],
          searchCriteria: [],
        };

        const fetchBoardRes = yield call(U.sendRequest, 'post', ENDPOINTS.fetchBoard, { data: reqData });

        const { data: fetchBoardData, error: fetchBoardError, status: fetchBoardStatus } = fetchBoardRes;

        if (U.isResponseSuccess(fetchBoardStatus)) {
          if (R.equals(0, page)) {
            yield put(boardRoutine.fetchSuccess(R.mergeLeft(fetchBoardData, { page, size: 50 })));
          } else {
            const boardList = yield select(makeSelectBoardList());

            yield put(boardRoutine.fetchSuccess(R.mergeLeft(
              R.evolve({ elements: R.concat(boardList) }, fetchBoardData),
              { page, size: 50 },
            )));
          }
        } else {
          yield put(boardRoutine.fetchFailure());

          yield call(U.showToastrMessage, 'error', fetchBoardError);
          yield call(U.handleFailResponse, fetchBoardRes, 'handleFetchBoardSaga fail', true);
        }
      }
    } else {
      yield put(fetchReportsListData.failure());

      yield call(U.showToastrMessage, 'error', error);
      yield call(U.handleFailResponse, fetchReportsListRes, 'handleFetchReportsListSaga fail', true);
    }
  } catch (error) {
    yield call(U.showToastrMessage, 'error', error.message);
    yield call(U.handleException, error, 'handleFetchBoardSaga exception');
  }
}

function* handleCreateUsedReportSaga({ payload }) {
  try {
    yield put(loaderRoutine.show());

    const res = yield call(U.sendRequest, 'post', ENDPOINTS.reportConfig, { data: payload });

    const { data, error, status } = res;

    if (U.isResponseSuccess(status)) {
      yield put(addUsedReportRoutine.success(data));

      const fetchReportsListRes = yield call(U.sendRequest, 'get', ENDPOINTS.reportConfig);

      const { data: reportsListData, error: reportsListError, status: reportsListStatus } = fetchReportsListRes;

      if (U.isResponseSuccess(reportsListStatus)) {
        yield put(fetchReportsListData.success(reportsListData));

        const reqData = {
          page: 0,
          size: 50,
          fields: R.pathOr([], ['fields'], payload),
          uuid: R.pathOr([], [C.FIELD_UUID], data),
          orderFields: R.pathOr([], ['orderFields'], payload),
          searchCriteria: R.pathOr([], ['searchCriteria'], payload),
        };

        yield put(boardRoutine(reqData));
        yield put(loaderRoutine.hide());
      } else {
        yield put(loaderRoutine.hide());
        yield put(fetchReportsListData.failure());

        yield call(U.showToastrMessage, 'error', reportsListError);
        yield call(U.handleFailResponse, fetchReportsListRes, 'handleFetchReportsListSaga fail', true);
      }
    } else {
      yield put(loaderRoutine.hide());
      yield put(addUsedReportRoutine.failure());

      yield call(U.showToastrMessage, 'error', error);
      yield call(U.handleFailResponse, res, 'handleCreateUsedReportSaga fail', true);
    }
  } catch (error) {
    yield put(loaderRoutine.hide());

    yield call(U.showToastrMessage, 'error', error.message);
    yield call(U.handleException, error, 'handleCreateUsedReportSaga exception');
  }
}

function* handleUpdateUsedReportSaga({ payload }) {
  try {
    yield put(loaderRoutine.show());

    const res = yield call(U.sendRequest, 'put', ENDPOINTS.reportConfig, { data: payload });

    const { data, error, status } = res;

    if (U.isResponseSuccess(status)) {
      yield put(editUsedReportRoutine.success(data));

      const fetchReportsListRes = yield call(U.sendRequest, 'get', ENDPOINTS.reportConfig);

      const { data: reportsListData, error: reportsListError, status: reportsListStatus } = fetchReportsListRes;

      if (U.isResponseSuccess(reportsListStatus)) {
        yield put(fetchReportsListData.success(reportsListData));

        const reqData = {
          page: 0,
          size: 50,
          fields: R.pathOr([], ['fields'], payload),
          uuid: R.pathOr([], [C.FIELD_UUID], payload),
          orderFields: R.pathOr([], ['orderFields'], payload),
          searchCriteria: R.pathOr([], ['searchCriteria'], payload),
        };

        yield put(boardRoutine(reqData));
        yield put(loaderRoutine.hide());
      } else {
        yield put(loaderRoutine.hide());
        yield put(fetchReportsListData.failure());

        yield call(U.showToastrMessage, 'error', reportsListError);
        yield call(U.handleFailResponse, fetchReportsListRes, 'handleFetchReportsListSaga fail', true);
      }
    } else {
      yield put(loaderRoutine.hide());
      yield put(editUsedReportRoutine.failure());

      yield call(U.showToastrMessage, 'error', error);
      yield call(U.handleFailResponse, res, 'handleUpdateUsedReportSaga fail', true);
    }
  } catch (error) {
    yield put(loaderRoutine.hide());

    yield call(U.showToastrMessage, 'error', error.message);
    yield call(U.handleException, error, 'handleUpdateUsedReportSaga exception');
  }
}

function* handleDeleteUsedReportSaga({ payload }) {
  try {
    yield put(loaderRoutine.show());

    const res = yield call(U.sendRequest, 'delete', ENDPOINTS.getReportConfigByUUID(payload));

    const { error, status } = res;

    if (U.isResponseSuccess(status)) {
      yield put(deleteUsedReportRoutine.success());

      const reqData = {
        page: 0,
        size: 50,
        fields: [],
        orders: [],
        orderFields: [],
      };

      yield put(boardRoutine(reqData));
      yield put(loaderRoutine.hide());
    } else {
      yield put(loaderRoutine.hide());
      yield put(deleteUsedReportRoutine.failure());

      yield call(U.showToastrMessage, 'error', error);
      yield call(U.handleFailResponse, res, 'handleDeleteUsedReportSaga fail', true);
    }
  } catch (error) {
    yield put(loaderRoutine.hide());

    yield call(U.showToastrMessage, 'error', error.message);
    yield call(U.handleException, error, 'handleDeleteUsedReportSaga exception');
  }
}

function* handleVisitBoardPageSaga() {
  const loggedIn = yield select(makeSelectLoggedIn());

  const { page } = yield select(makeSelectBoardListPagination());

  if (R.not(loggedIn)) yield take(loginRoutine.SUCCESS);

  yield put(boardRoutine.fetch({
    page,
    size: 50,
    fields: [],
    orders: [],
    orderFields: [],
  }));

  yield put(headerFileUploadStatusData());
}

function* boardWatcherSaga() {
  yield takeLatest(C.VISIT_HOME_PAGE, handleVisitBoardPageSaga);
  yield takeLatest(boardRoutine.fetch, handleFetchBoardSaga);
  yield takeLatest(addUsedReportRoutine, handleCreateUsedReportSaga);
  yield takeLatest(editUsedReportRoutine, handleUpdateUsedReportSaga);
  yield takeLatest(deleteUsedReportRoutine, handleDeleteUsedReportSaga);
}

export default boardWatcherSaga;
